/**
 * KidsCountFilter Component
 * 
 * "How many kids do you have?" filter section with +/- counter buttons.
 * Range: 1-16 kids
 */

import { MAX_KIDS, MIN_KIDS, useFilter } from '../../../contexts/FilterContext';
import FilterSection from './FilterSection';
import { HOME_V2_COLORS } from '../colors';


/**
 * Counter Button Component
 * 
 * Pill-shaped button for increment/decrement actions.
 */
interface CounterButtonProps {
  label: '+' | '-';
  onClick: () => void;
  disabled: boolean;
}

function CounterButton({ label, onClick, disabled }: CounterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        w-[115px] h-12
        flex items-center justify-center
        rounded-lg
        text-2xl font-semibold
        transition-all duration-150
        focus:outline-none
        focus:ring-2
        focus:ring-offset-2
        disabled:opacity-30
        disabled:cursor-not-allowed
      `}
      style={{
        backgroundColor: HOME_V2_COLORS.pillUnselectedBg,
        color: HOME_V2_COLORS.pillUnselectedText,
        '--tw-ring-color': HOME_V2_COLORS.pillUnselectedText,
      } as React.CSSProperties}
      aria-label={label === '+' ? 'Increase kids count' : 'Decrease kids count'}
    >
      {label}
    </button>
  );
}

export default function KidsCountFilter() {
  const { filters, setKidsCount } = useFilter();
  const currentCount = filters.kidsCount;

  const handleDecrement = () => {
    if (currentCount > MIN_KIDS) {
      setKidsCount(currentCount - 1);
    }
  };

  const handleIncrement = () => {
    if (currentCount < MAX_KIDS) {
      setKidsCount(currentCount + 1);
    }
  };

  return (
    <FilterSection title="How many kids do you have?">
      <div className="flex items-center justify-between">
        {/* Decrement Button */}
        <CounterButton
          label="-"
          onClick={handleDecrement}
          disabled={currentCount <= MIN_KIDS}
        />
        
        {/* Count Display */}
        <div 
          className="flex-1 text-center mx-4"
          aria-live="polite"
          aria-atomic="true"
        >
          <span 
            className="text-2xl font-semibold"
            style={{ color: HOME_V2_COLORS.pillUnselectedText }}
          >
            {currentCount}
          </span>
        </div>
        
        {/* Increment Button */}
        <CounterButton
          label="+"
          onClick={handleIncrement}
          disabled={currentCount >= MAX_KIDS}
        />
      </div>
    </FilterSection>
  );
}
